import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../utils/api-client';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import type { User } from '../types/user';

const TOKEN_KEY = 'climb_log_auth_token';
const REFRESH_TOKEN_KEY = 'climb_log_refresh_token';
const USER_KEY = 'climb_log_user';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const resetPasswordToken = searchParams.get('reset_password_token');

  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);

  const { t } = useTranslation();

  useEffect(() => {
    if (!resetPasswordToken) {
      setTokenError(t('auth.resetPasswordTokenMissing'));
    }
  }, [resetPasswordToken, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || !passwordConfirmation) return;

    setFormError(null);

    if (password !== passwordConfirmation) {
      setFormError(t('auth.passwordsDontMatch'));
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiClient.put('/api/users/password', {
        reset_password_token: resetPasswordToken,
        password,
        password_confirmation: passwordConfirmation,
      }, { showErrors: false });

      if (!response.ok) {
        const errorData = await response.json() as { error?: string };
        throw new Error(errorData.error || t('auth.unexpectedError'));
      }

      const data = await response.json() as { token: string; refresh_token: string; resource_owner: { user?: User } | User };
      const resourceOwner = data.resource_owner;
      const userData = ('user' in resourceOwner && resourceOwner.user) ? resourceOwner.user : resourceOwner as User;

      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
      localStorage.setItem(USER_KEY, JSON.stringify(userData));

      window.location.href = '/';
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('auth.unexpectedError');
      setFormError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (tokenError) {
    return (
      <div className="flex justify-center items-center my-8">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t('auth.resetPasswordInvalidTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="p-3 rounded-md bg-destructive/15 text-destructive text-sm">
              {tokenError}
            </div>
          </CardContent>
          <CardFooter>
            <Link to="/forgot-password" className="w-full">
              <Button variant="outline" className="w-full">
                {t('auth.requestNewLink')}
              </Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center my-8">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('auth.resetPasswordTitle')}</CardTitle>
          <CardDescription>{t('auth.resetPasswordDescription')}</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {formError && (
              <div className="p-3 rounded-md bg-destructive/15 text-destructive text-sm">
                {formError}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.newPassword')}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setPassword(e.target.value);
                  if (formError) setFormError(null);
                }}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="passwordConfirmation">{t('auth.confirmPassword')}</Label>
              <Input
                id="passwordConfirmation"
                type="password"
                value={passwordConfirmation}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setPasswordConfirmation(e.target.value);
                  if (formError) setFormError(null);
                }}
                required
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? t('auth.resettingPassword') : t('auth.resetPassword')}
            </Button>
            <p className="text-sm text-center text-muted-foreground">
              <Link to="/sign-in" className="text-primary hover:underline">
                {t('auth.backToSignIn')}
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
